import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { InvoiceService } from '../../../../core/api/invoice.service';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import {
  BaseInvoiceUpdate,
  CommodityType,
  InvoiceBimestraleGet,
  InvoiceBimestraleUpdate,
} from '../../../../core/interfaces/invoice-interfaces';
import { DropDownSectionComponent } from '../../../../shared/components/drop-down-section/drop-down-section.component';
import { EditCancelButtonComponent } from '../../../../shared/components/edit-cancel-button/edit-cancel-button.component';
import { LabeledInputComponent } from '../../../../shared/components/labeled-input/labeled-input.component';
import { CustomNumberInputComponent } from '../../../../shared/components/custom-number-input/custom-number-input.component';

@Component({
  selector: 'app-edit-bimestrale-summary',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoPipe,
    DropDownSectionComponent,
    EditCancelButtonComponent,
    LabeledInputComponent,
    CustomNumberInputComponent,
  ],
  templateUrl: './edit-bimestrale-summary.component.html',
  styleUrl: './edit-bimestrale-summary.component.css'
})
export class EditBimestraleSummaryComponent implements OnChanges {
  constructor(
    private invoiceService: InvoiceService,
    private apiError: ApiErrorService,
  ) { }

  @Input() bimestraleData: InvoiceBimestraleGet | null = null;
  @Input() fileInvoices: any[] = [];
  @Input() toast!: ToastComponent;
  @Input() commodityType!: CommodityType;

  @Output() fileInvoicesChange = new EventEmitter<any[]>();
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  isEditingBimestrale: boolean = false;
  savingBimestrale: boolean = false;

  editedBimestrale: {
    for_period_start?: string;
    for_period_end?: string;
    invoice_number?: string;
    total_consumption?: string;
    invoice_amount_net_actual?: string;
    invoice_amount_gross_actual?: string;
    vat_rate?: string;
    vat_amount_actual?: string;
    supplier_name?: string;
    additional_charges?: string;
    total_discount_amount?: string;
    tms_charges?: string;
    total_taxes?: string;
    nontaxable_amount?: string;
  } = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bimestraleData']) {
      this.resetBimestraleData();
    }
  }

  resetBimestraleData() {
    if (!this.bimestraleData) return;
    this.editedBimestrale = {
      for_period_start: this.bimestraleData.for_period_start ?? undefined,
      for_period_end: this.bimestraleData.for_period_end ?? undefined,
      invoice_number: this.bimestraleData.invoice_number ?? undefined,
      total_consumption: this.bimestraleData.total_consumption?.toString() ?? undefined,
      invoice_amount_net_actual: this.bimestraleData.invoice_amount_net_actual?.toString() ?? undefined,
      invoice_amount_gross_actual: this.bimestraleData.invoice_amount_gross_actual?.toString() ?? undefined,
      vat_rate: this.bimestraleData.vat_rate?.toString() ?? undefined,
      vat_amount_actual: this.bimestraleData.vat_amount_actual?.toString() ?? undefined,
      supplier_name: this.bimestraleData.supplier_name ?? undefined,
      additional_charges: this.bimestraleData.additional_charges?.toString() ?? undefined,
      total_discount_amount: this.bimestraleData.total_discount_amount?.toString() ?? undefined,
      tms_charges: this.bimestraleData.tms_charges?.toString() ?? undefined,
      total_taxes: this.bimestraleData.total_taxes?.toString() ?? undefined,
      nontaxable_amount: this.bimestraleData.nontaxable_amount?.toString() ?? undefined,
    };
  }

  editBimestrale() {
    this.isEditingBimestrale = true;
  }

  cancelEditBimestrale() {
    this.isEditingBimestrale = false;
    this.resetBimestraleData();
    this.cancelled.emit();
  }

  syncFieldToInvoices(field: string, value: string) {
    const updated = this.fileInvoices.map(inv => {
      const copy = { ...inv };
      if (field === 'invoice_number') copy.invoice_number = value || null;
      if (field === 'supplier_name') copy.supplier_name = value || null;
      return copy;
    });
    this.fileInvoicesChange.emit(updated);
  }

  async saveBimestraleData() {
    if (!this.bimestraleData?.id) return;
    this.savingBimestrale = true;
    try {
      const update: InvoiceBimestraleUpdate = {
        for_period_start: this.editedBimestrale.for_period_start ?? null,
        for_period_end: this.editedBimestrale.for_period_end ?? null,
        invoice_number: this.editedBimestrale.invoice_number ?? null,
        total_consumption: this.editedBimestrale.total_consumption ? parseFloat(this.editedBimestrale.total_consumption) : null,
        invoice_amount_net_actual: this.editedBimestrale.invoice_amount_net_actual ? parseFloat(this.editedBimestrale.invoice_amount_net_actual) : null,
        invoice_amount_gross_actual: this.editedBimestrale.invoice_amount_gross_actual ? parseFloat(this.editedBimestrale.invoice_amount_gross_actual) : null,
        vat_rate: this.editedBimestrale.vat_rate ? parseFloat(this.editedBimestrale.vat_rate) : null,
        vat_amount_actual: this.editedBimestrale.vat_amount_actual ? parseFloat(this.editedBimestrale.vat_amount_actual) : null,
        supplier_name: this.editedBimestrale.supplier_name ?? null,
        additional_charges: this.editedBimestrale.additional_charges ? parseFloat(this.editedBimestrale.additional_charges) : null,
        total_discount_amount: this.editedBimestrale.total_discount_amount ? parseFloat(this.editedBimestrale.total_discount_amount) : null,
        tms_charges: this.editedBimestrale.tms_charges ? parseFloat(this.editedBimestrale.tms_charges) : null,
        total_taxes: this.editedBimestrale.total_taxes ? parseFloat(this.editedBimestrale.total_taxes) : null,
        nontaxable_amount: this.editedBimestrale.nontaxable_amount ? parseFloat(this.editedBimestrale.nontaxable_amount) : null,
      };
      await lastValueFrom(this.invoiceService.updateBimestrale(this.bimestraleData.id, update));

      // Sync shared fields to each per-period invoice
      for (const inv of this.fileInvoices) {
        const invoiceUpdate: BaseInvoiceUpdate = {
          id: inv.id,
          location_id: inv.location_id,
          for_period: inv.for_period,
          invoice_number: update.invoice_number ?? inv.invoice_number,
          total_consumption: inv.total_consumption,
          invoice_amount_net_actual: inv.invoice_amount_net_actual,
          total_taxes: inv.total_taxes,
        };
        if (this.commodityType === CommodityType.electricity) {
          await lastValueFrom(this.invoiceService.updateElectricityInvoice(invoiceUpdate));
        } else {
          await lastValueFrom(this.invoiceService.updateGasInvoice(invoiceUpdate));
        }
      }

      this.isEditingBimestrale = false;
      this.toast.showToast('shared.success', 'admin.bimestrale_updated', 'success', 3000, true);
      this.saved.emit();
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }
    this.savingBimestrale = false;
  }
}
